import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestoreIcon from "@mui/icons-material/Restore";
import { Task, Project } from "./taskboard-reducer";

const STAGES = [
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "In Progress" },
  { value: "blocked", label: "Blocker" },
  { value: "done", label: "Done" },
];

const PRIORITIES = [
  { value: "", label: "None" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (fields: any) => void;
  onDelete?: () => void;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onDuplicate?: () => void;
  task?: Task | null;
  projects: Project[];
  users: any[];
  defaultStage?: Task["stage"];
}

const TaskModal = ({ open, onClose, onSave, onDelete, onArchive, onUnarchive, onDuplicate, task, projects, users, defaultStage = "todo" }: Props) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [stage, setStage] = useState<Task["stage"]>(defaultStage);
  const [priority, setPriority] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [projectId, setProjectId] = useState<number | "">("");
  const [assigneeId, setAssigneeId] = useState<number | "">("");

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || "");
      setStage(task.stage);
      setPriority(task.priority || "");
      setDueDate(task.dueDate || "");
      setProjectId(task.projectId);
      setAssigneeId(task.assigneeId || "");
    } else {
      setTitle("");
      setDescription("");
      setStage(defaultStage);
      setPriority("");
      setDueDate("");
      setProjectId(projects.length === 1 ? projects[0].id : "");
      setAssigneeId("");
    }
  }, [task, open, defaultStage, projects]);

  const handleSave = () => {
    if (!title.trim() || !projectId) return;
    onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      stage,
      priority: priority || undefined,
      dueDate: dueDate || undefined,
      projectId,
      assigneeId: assigneeId || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        {task ? "Edit Task" : "New Task"}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          autoFocus
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl fullWidth required>
            <InputLabel>Project</InputLabel>
            <Select
              value={projectId}
              label="Project"
              onChange={(e) => setProjectId(e.target.value as number)}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Stage</InputLabel>
            <Select
              value={stage}
              label="Stage"
              onChange={(e) => setStage(e.target.value as Task["stage"])}
            >
              {STAGES.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={3}
        />

        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={priority}
              label="Priority"
              onChange={(e) => setPriority(e.target.value)}
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {users.length > 0 && (
          <FormControl fullWidth>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={assigneeId}
              label="Assignee"
              onChange={(e) => setAssigneeId(e.target.value as number)}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {task && (
          <Box sx={{ display: "flex", gap: 1, mr: "auto" }}>
            {onDuplicate && (
              <Button
                onClick={onDuplicate}
                startIcon={<ContentCopyIcon />}
                color="inherit"
                variant="outlined"
                size="small"
                sx={{ color: "#64748b", borderColor: "#cbd5e1" }}
              >
                Duplicate
              </Button>
            )}
            {onArchive && (
              <Button
                onClick={onArchive}
                startIcon={<ArchiveIcon />}
                color="inherit"
                variant="outlined"
                size="small"
                sx={{ color: "#64748b", borderColor: "#cbd5e1" }}
              >
                Archive
              </Button>
            )}
            {onUnarchive && (
              <Button
                onClick={onUnarchive}
                startIcon={<RestoreIcon />}
                color="inherit"
                variant="outlined"
                size="small"
                sx={{ color: "#64748b", borderColor: "#cbd5e1" }}
              >
                Unarchive
              </Button>
            )}
            {onDelete && (
              <Button
                onClick={onDelete}
                startIcon={<DeleteIcon />}
                color="error"
                variant="outlined"
                size="small"
              >
                Delete
              </Button>
            )}
          </Box>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!title.trim() || !projectId}>
          {task ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskModal;
